/**
 * NotificationPreferencesPage - User-facing page for managing notification settings
 * 
 * Provides a complete interface for users to control their email notification
 * preferences including frequency, timing, and timezone settings.
 */
import React from 'react';
import { Bell, Info } from 'lucide-react';
import Card from '../components/Card';
import { NotificationPreferencesForm } from '../components/notifications/NotificationPreferencesForm';
import { useNotificationPreferences } from '../hooks/notifications/useNotificationPreferences';
import { CardSkeleton } from '../components/LoadingSkeleton';

const NotificationPreferencesPage: React.FC = () => {
  const { preferences, loading, error, updatePreferences } = useNotificationPreferences();

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-primary-600 via-secondary-600 to-accent-600 bg-clip-text text-transparent">
            Notification Preferences
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Loading your notification settings...
          </p>
        </div>
        <CardSkeleton />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Page Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-3">
          <Bell className="w-10 h-10 text-primary-600 dark:text-primary-400" />
          <h1 className="text-4xl font-bold bg-gradient-to-r from-primary-600 via-secondary-600 to-accent-600 bg-clip-text text-transparent">
            Notification Preferences
          </h1>
        </div>
        <p className="text-gray-600 dark:text-gray-400 text-lg">
          Manage when and how you receive task notifications via email
        </p>
      </div>

      {/* Error Loading Preferences */}
      {error && !preferences && (
        <div className="mb-6 rounded-lg p-4 bg-error/10 dark:bg-error/20 border border-error/30 text-error">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Preferences Form Card */}
      <Card variant="default">
        <Card.Header
          title="Email Notification Settings"
          subtitle="Configure your notification preferences and schedule"
        />
        <Card.Body>
          <NotificationPreferencesForm
            preferences={preferences}
            onSubmit={updatePreferences}
            loading={loading}
          />
        </Card.Body>
      </Card>

      {/* Information Card */}
      <Card variant="default" className="mt-6">
        <Card.Body>
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-primary-600 dark:text-primary-300 flex-shrink-0 mt-0.5" />
            <div className="space-y-3 text-sm text-gray-700 dark:text-gray-300">
              <h3 className="font-semibold text-gray-900 dark:text-white">
                How Notifications Work
              </h3>
              <ul className="list-disc list-inside space-y-2 pl-2">
                <li>
                  <strong>Daily notifications:</strong> Receive an email every day at your selected time with overdue and upcoming tasks
                </li>
                <li>
                  <strong>Weekly notifications:</strong> Receive an email once per week (Monday) with a summary of tasks for the week
                </li>
                <li>
                  <strong>Timezone-aware:</strong> Notifications are scheduled in your local timezone for accurate delivery
                </li>
                <li>
                  <strong>Smart filtering:</strong> Only receive notifications when you have tasks due or overdue
                </li>
                <li>
                  <strong>In-app notifications:</strong> The notification banner on the dashboard shows real-time counts (separate from email settings)
                </li>
              </ul>
              
              <div className="mt-4 p-3 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg">
                <p className="text-primary-900 dark:text-primary-100">
                  <strong>💡 Tip:</strong> If you disable email notifications, you'll still see in-app notifications on your dashboard when you login.
                </p>
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default NotificationPreferencesPage;
